import { getPartnerToken } from '@/services/auth/partnerSessionStorage';
import axios from 'axios';
import { getDeviceKey } from '@/services/device/deviceCredential';
import { OFFLINE_MODE_ENABLED } from '@/services/offline/offlineFeature';
import { getHubProfile, saveHubProfile } from './hubCredentialStore';

export const HUB_DISCOVERY_STATES = Object.freeze({
  NOT_CONFIGURED: 'NOT_CONFIGURED',
  DISCOVERING: 'DISCOVERING',
  REACHABLE_UNPAIRED: 'REACHABLE_UNPAIRED',
  PAIRING: 'PAIRING',
  CONNECTED: 'CONNECTED',
  UNREACHABLE: 'UNREACHABLE',
  CERTIFICATE_ERROR: 'CERTIFICATE_ERROR',
  PROTOCOL_INCOMPATIBLE: 'PROTOCOL_INCOMPATIBLE',
  REVOKED: 'REVOKED',
});

const REQUEST_TIMEOUT_MS = 2500;

function partnerToken(store) {
  return store?.state?.partner_token || getPartnerToken();
}

function normalizeUrl(value) {
  return String(value || '').trim().replace(/\/+$/, '');
}

export function targetAddressSpaceForUrl(url) {
  try {
    const hostname = new URL(normalizeUrl(url)).hostname;
    return ['localhost', '127.0.0.1', '::1'].includes(hostname)
      ? 'loopback'
      : 'local';
  } catch {
    return 'local';
  }
}

async function requestIdentity(url) {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(`${normalizeUrl(url)}/local/v1/identity`, {
      method: 'GET',
      credentials: 'omit',
      mode: 'cors',
      signal: controller.signal,
      // Chrome 142+ uses this hint to request Local Network Access
      // instead of reporting an opaque network failure.
      targetAddressSpace: targetAddressSpaceForUrl(url),
      headers: { 'X-EWU-Local-Probe': '1' },
    });
    if (!response.ok) {
      const error = new Error(`El Hub respondió HTTP ${response.status}.`);
      error.response = { status: response.status };
      throw error;
    }
    return await response.json();
  } finally {
    window.clearTimeout(timeout);
  }
}

function loopbackCandidate(profile) {
  if (
    profile?.secure
    || !['localhost', '127.0.0.1', '::1'].includes(window.location.hostname)
  ) {
    return '';
  }
  return `http://127.0.0.1:${Number(profile.lan_port || 8765)}`;
}

function localNetworkPermissionError(error) {
  return !error?.response
    && /failed to fetch|networkerror|network error/i.test(
      String(error?.message || ''),
    );
}

function discoveryError(error, url, browserOrigin) {
  return {
    code: error?.response?.status === 403
      ? 'ORIGIN_NOT_ALLOWED'
      : localNetworkPermissionError(error)
        ? 'LOCAL_NETWORK_ACCESS_REQUIRED'
        : certificateLikeError(error, url)
          ? 'CERTIFICATE_ERROR'
          : 'HUB_UNREACHABLE',
    message: error?.name === 'AbortError'
      ? 'La conexión con el Hub superó el tiempo de espera.'
      : error?.message || 'No se pudo conectar con el Hub.',
    url: normalizeUrl(url),
    origin: browserOrigin,
    status: error?.response?.status || null,
  };
}

function certificateLikeError(error, url) {
  return String(url).startsWith('https://')
    && !error?.response
    && /certificate|cert|ssl|tls/i.test(String(error?.message || ''));
}

export async function loadCloudHubProfile(store) {
  if (!OFFLINE_MODE_ENABLED) return null;
  const token = partnerToken(store);
  const state = store.state.device;
  if (!token || !state.deviceId) return null;
  const deviceKey = await getDeviceKey(state.deviceId);
  if (!deviceKey) return null;
  const response = await axios.get(
    `${process.env.VUE_APP_API_URL}/v1/api/tpv/local-hubs/connection-profile`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-EWU-Device-Key': deviceKey,
      },
      timeout: 5000,
    },
  );
  if (!response.data?.hub_configured) return response.data;
  await saveHubProfile(response.data);
  return response.data;
}

export async function discoverHub(store, options = {}) {
  if (!OFFLINE_MODE_ENABLED) {
    const result = { state: HUB_DISCOVERY_STATES.NOT_CONFIGURED, profile: null };
    store.commit('device/setLocalControlDiscovery', result);
    return result;
  }
  const browserOrigin = window.location.origin;
  store.commit('device/setLocalControlDiscovery', {
    state: HUB_DISCOVERY_STATES.DISCOVERING,
    last_error: null,
    browser_origin: browserOrigin,
    attempted_urls: [],
  });
  let profile = options.profile || null;
  if (!profile && options.useCloud !== false) {
    try {
      profile = await loadCloudHubProfile(store);
    } catch {
      // Cached/manual discovery remains available when cloud is unavailable.
    }
  }
  profile = profile || await getHubProfile();
  if (!profile?.hub_configured && !profile?.hub_uuid) {
    const result = { state: HUB_DISCOVERY_STATES.NOT_CONFIGURED, profile: null };
    store.commit('device/setLocalControlDiscovery', result);
    return result;
  }
  const manual = normalizeUrl(options.manualUrl);
  const urls = [
    manual,
    profile.emergency_profile?.hub_endpoint,
    profile.last_emergency_url,
    loopbackCandidate(profile),
    profile.last_valid_url,
    ...(profile.candidate_urls || []),
  ].filter(Boolean);
  let lastError = null;
  const attemptedUrls = [];
  for (const url of [...new Set(urls)]) {
    attemptedUrls.push(normalizeUrl(url));
    try {
      const identity = await requestIdentity(url);
      if (String(identity.hub_uuid) !== String(profile.hub_uuid)) {
        lastError = { code: 'HUB_IDENTITY_MISMATCH', message: 'La identidad del Hub no coincide.' };
        continue;
      }
      if (
        profile.certificate_fingerprint
        && String(identity.certificate_fingerprint || '').toLowerCase()
          !== String(profile.certificate_fingerprint).toLowerCase()
      ) {
        const result = {
          state: HUB_DISCOVERY_STATES.CERTIFICATE_ERROR,
          profile,
          identity,
          url,
          last_error: { code: 'CERTIFICATE_FINGERPRINT_MISMATCH' },
        };
        store.commit('device/setLocalControlDiscovery', result);
        return result;
      }
      const expected = Number(profile.control_protocol_version || 1);
      const actual = Number(identity.protocol_version || 0);
      if (actual !== expected) {
        const result = {
          state: HUB_DISCOVERY_STATES.PROTOCOL_INCOMPATIBLE,
          profile,
          identity,
          url,
          last_error: { code: 'CONTROL_PROTOCOL_INCOMPATIBLE' },
        };
        store.commit('device/setLocalControlDiscovery', result);
        return result;
      }
      if (profile.secure && !String(url).startsWith('https://')) {
        lastError = { code: 'INSECURE_TRANSPORT_REJECTED', message: 'El perfil exige HTTPS.' };
        continue;
      }
      const validated = {
        ...profile,
        last_valid_url: normalizeUrl(url),
        last_identity_at: new Date().toISOString(),
      };
      await saveHubProfile(validated);
      const result = {
        state: HUB_DISCOVERY_STATES.REACHABLE_UNPAIRED,
        profile: validated,
        identity,
        url: validated.last_valid_url,
        last_connected_at: new Date().toISOString(),
        browser_origin: browserOrigin,
        attempted_urls: attemptedUrls,
        last_error: null,
      };
      store.commit('device/setLocalControlDiscovery', result);
      return result;
    } catch (error) {
      lastError = discoveryError(error, url, browserOrigin);
    }
  }
  const result = {
    state: lastError?.code === 'CERTIFICATE_ERROR'
      ? HUB_DISCOVERY_STATES.CERTIFICATE_ERROR
      : HUB_DISCOVERY_STATES.UNREACHABLE,
    profile,
    browser_origin: browserOrigin,
    attempted_urls: attemptedUrls,
    last_error: lastError,
  };
  store.commit('device/setLocalControlDiscovery', result);
  return result;
}

export async function saveManualHubUrl(profile, manualUrl) {
  const updated = {
    ...profile,
    candidate_urls: [
      normalizeUrl(manualUrl),
      ...(profile?.candidate_urls || []),
    ].filter(Boolean),
  };
  await saveHubProfile(updated);
  return updated;
}

export async function saveEmergencyHubProfile(profile, emergencyProfile) {
  if (!profile?.hub_uuid || !emergencyProfile?.hub_endpoint) return profile;
  const endpoint = normalizeUrl(emergencyProfile.hub_endpoint);
  const updated = {
    ...profile,
    emergency_profile: {
      ...(profile.emergency_profile || {}),
      ...emergencyProfile,
      hub_endpoint: endpoint,
    },
    last_emergency_url: endpoint,
    candidate_urls: [
      endpoint,
      ...(profile.candidate_urls || []),
    ].filter(Boolean),
  };
  await saveHubProfile(updated);
  return updated;
}
